import { BottomSheet, Button, Card, ErrorState, Field, LoadingState } from '@beautyburst/ui';
import { minTouchTarget, semanticColor, space } from '@beautyburst/tokens';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { MfaStatus, MfaStepUp, RetailerApiClient } from './api-client.ts';
import { RetailerApiError } from './http-client.ts';

type MfaClient = RetailerApiClient['mfa'];

type Remote<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'loaded'; readonly value: T }
  | { readonly state: 'failed'; readonly error: unknown };

function messageFor(error: unknown): string {
  return error instanceof RetailerApiError
    ? error.message
    : 'The security request could not be completed. Try again.';
}

async function copyToClipboard(value: string): Promise<void> {
  if (navigator.clipboard?.writeText !== undefined) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  document.body.append(input);
  input.select();
  document.execCommand('copy');
  input.remove();
}

/** Coordinates the MFA dialog with an interrupted protected portal request. */
export function useMfaStepUpChallenge() {
  const [pending, setPending] = useState<{
    readonly resolve: () => void;
    readonly reject: () => void;
  } | null>(null);

  const requestStepUp = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        setPending({ resolve, reject });
      }),
    [],
  );
  const complete = useCallback(() => {
    setPending((current) => {
      current?.resolve();
      return null;
    });
  }, []);
  const close = useCallback(() => {
    setPending((current) => {
      current?.reject();
      return null;
    });
  }, []);
  return { open: pending !== null, requestStepUp, complete, close };
}

export function MfaStepUpDialog({
  client,
  open,
  onComplete,
  onClose,
}: {
  readonly client: Pick<MfaClient, 'status' | 'beginTotp' | 'confirmTotp' | 'stepUp'>;
  readonly open: boolean;
  readonly onComplete: (value: MfaStepUp) => void;
  readonly onClose: () => void;
}) {
  const [status, setStatus] = useState<Remote<MfaStatus>>({ state: 'loading' });
  const [stage, setStage] = useState<'enrollment' | 'recovery' | 'verification'>('verification');
  const [uri, setUri] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const refreshStatus = useCallback(() => {
    setStatus({ state: 'loading' });
    setError(null);
    void client.status().then(
      (value) => {
        setStatus({ state: 'loaded', value });
        setStage(value.status === 'ACTIVE' ? 'verification' : 'enrollment');
      },
      (reason: unknown) => setStatus({ state: 'failed', error: reason }),
    );
  }, [client]);

  useEffect(() => {
    if (open) {
      refreshStatus();
      return;
    }
    setStatus({ state: 'loading' });
    setStage('verification');
    setUri(null);
    setRecoveryCodes(null);
    setCode('');
    setError(null);
    setNotice(null);
    setSubmitting(false);
  }, [open, refreshStatus]);

  const begin = async () => {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const response = await client.beginTotp();
      setUri(response.provisioningUri);
      setRecoveryCodes(null);
      setCode('');
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (code.trim() === '') return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await client.confirmTotp(code);
      // Keep these one-time values only in this open dialog's component memory.
      setRecoveryCodes(response.recoveryCodes);
      setUri(null);
      setCode('');
      setStage('recovery');
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (code.trim() === '') return;
    setSubmitting(true);
    setError(null);
    try {
      onComplete(await client.stepUp(code));
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet open={open} title="Verify this session" onClose={onClose}>
      {error === null ? null : <p role="alert">{error}</p>}
      {notice === null ? null : <p role="status">{notice}</p>}
      {status.state === 'loading' ? <LoadingState label="Loading security status" /> : null}
      {status.state === 'failed' ? (
        <ErrorState
          title="Security status unavailable"
          description={messageFor(status.error)}
          retry={
            <Button
              onClick={refreshStatus}
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            >
              Retry security status
            </Button>
          }
        />
      ) : null}
      {status.state !== 'loaded' || stage !== 'enrollment' ? null : (
        <>
          <p>
            Set up an authenticator app before verifying this session. Your protected request will
            remain pending until verification succeeds.
          </p>
          {uri === null ? (
            <Button
              onClick={() => void begin()}
              disabled={submitting}
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            >
              {submitting ? 'Starting enrollment…' : 'Set up an authenticator app'}
            </Button>
          ) : (
            <Card>
              <h2>1. Add Beauty Burst to your authenticator app</h2>
              <p>
                Scan or manually enter this provisioning URI. Keep it private while enrollment is in
                progress.
              </p>
              <textarea
                aria-label="Authenticator provisioning URI"
                value={uri}
                readOnly
                onFocus={(event) => event.currentTarget.select()}
                style={{
                  width: '100%',
                  minHeight: '6rem',
                  color: semanticColor.textPrimary,
                  background: semanticColor.surfaceSubtle,
                  border: `1px solid ${semanticColor.borderDefault}`,
                }}
              />
              <Button
                variant="secondary"
                onClick={() =>
                  void copyToClipboard(uri).then(
                    () => setNotice('Provisioning URI copied.'),
                    () =>
                      setError(
                        'The provisioning URI could not be copied. Select and copy it manually.',
                      ),
                  )
                }
                style={{ minWidth: minTouchTarget, minHeight: minTouchTarget, marginTop: space[3] }}
              >
                Copy provisioning URI
              </Button>
              <form
                onSubmit={(event) => void confirm(event)}
                style={{ display: 'grid', gap: space[4], marginTop: space[4] }}
              >
                <h3>2. Confirm setup</h3>
                <Field
                  id="retailer-mfa-step-up-enrollment-code"
                  name="code"
                  label="Authenticator app code"
                  value={code}
                  onChange={(event) => setCode(event.currentTarget.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
                <Button
                  type="submit"
                  disabled={submitting || code.trim() === ''}
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                >
                  {submitting ? 'Confirming…' : 'Confirm authenticator app'}
                </Button>
              </form>
            </Card>
          )}
        </>
      )}
      {status.state !== 'loaded' || stage !== 'recovery' || recoveryCodes === null ? null : (
        <Card>
          <h2>Recovery codes</h2>
          <p role="alert">
            Save these codes now. They will not be shown again, and each code can be used only once.
          </p>
          <ul aria-label="One-time recovery codes">
            {recoveryCodes.map((recoveryCode) => (
              <li key={recoveryCode}>
                <code>{recoveryCode}</code>
              </li>
            ))}
          </ul>
          <Button
            onClick={() => {
              setRecoveryCodes(null);
              setStage('verification');
            }}
            style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
          >
            I saved my recovery codes
          </Button>
        </Card>
      )}
      {status.state !== 'loaded' || stage !== 'verification' ? null : (
        <>
          <p>
            This action affects store operations, so confirm your identity with an authenticator app
            code or one of your recovery codes.
          </p>
          <form onSubmit={(event) => void submit(event)} style={{ display: 'grid', gap: space[4] }}>
            <Field
              id="retailer-mfa-step-up-code"
              name="code"
              label="Authenticator or recovery code"
              value={code}
              onChange={(event) => setCode(event.currentTarget.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
            />
            <Button
              type="submit"
              disabled={submitting || code.trim() === ''}
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            >
              {submitting ? 'Verifying…' : 'Verify and continue'}
            </Button>
          </form>
        </>
      )}
    </BottomSheet>
  );
}

export function RetailerSecurityScreen({
  client,
  onRequestStepUp,
}: {
  readonly client: MfaClient;
  readonly onRequestStepUp: () => Promise<void>;
}) {
  const [status, setStatus] = useState<Remote<MfaStatus>>({ state: 'loading' });
  const [uri, setUri] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const refresh = useCallback(() => {
    setStatus({ state: 'loading' });
    void client.status().then(
      (value) => setStatus({ state: 'loaded', value }),
      (reason: unknown) => setStatus({ state: 'failed', error: reason }),
    );
  }, [client]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const begin = async () => {
    setWorking(true);
    setError(null);
    setNotice(null);
    try {
      const response = await client.beginTotp();
      setUri(response.provisioningUri);
      setRecoveryCodes(null);
      setCode('');
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setWorking(false);
    }
  };
  const confirm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (code.trim() === '') return;
    setWorking(true);
    setError(null);
    try {
      const response = await client.confirmTotp(code);
      // Codes originate only in this immediate, successful enrollment response.
      setRecoveryCodes(response.recoveryCodes);
      setUri(null);
      setCode('');
      setNotice('Authenticator app enabled. Save your recovery codes now.');
      refresh();
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setWorking(false);
    }
  };

  return (
    <section aria-labelledby="retailer-security-heading">
      <h1 id="retailer-security-heading">Security</h1>
      <p>Manage multi-factor authentication for this retailer account.</p>
      {status.state === 'loading' ? <LoadingState label="Loading security status" /> : null}
      {status.state === 'failed' ? (
        <ErrorState
          title="Security status unavailable"
          description={messageFor(status.error)}
          retry={<Button onClick={refresh}>Retry security status</Button>}
        />
      ) : null}
      {status.state !== 'loaded' ? null : (
        <>
          <Card>
            <h2>Authenticator app</h2>
            <p role="status">
              {status.value.status === 'ACTIVE'
                ? 'Authenticator app is enabled.'
                : 'Authenticator app is not enabled.'}
            </p>
            <p>
              Current session:{' '}
              {status.value.mfaSatisfied ? 'verified for sensitive actions' : 'not yet verified'}.
            </p>
            {status.value.status === 'ACTIVE' && !status.value.mfaSatisfied ? (
              <Button
                onClick={() => void onRequestStepUp().catch(() => undefined)}
                style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              >
                Verify this session
              </Button>
            ) : null}
            {status.value.status !== 'ACTIVE' && uri === null ? (
              <Button
                onClick={() => void begin()}
                disabled={working}
                style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              >
                {working ? 'Starting enrollment…' : 'Set up an authenticator app'}
              </Button>
            ) : null}
          </Card>
          {uri === null ? null : (
            <Card>
              <h2>1. Add Beauty Burst to your authenticator app</h2>
              <p>
                Scan or manually enter this provisioning URI. Keep it private while enrollment is in
                progress.
              </p>
              <textarea
                aria-label="Authenticator provisioning URI"
                value={uri}
                readOnly
                onFocus={(event) => event.currentTarget.select()}
                style={{
                  width: '100%',
                  minHeight: '6rem',
                  color: semanticColor.textPrimary,
                  background: semanticColor.surfaceSubtle,
                  border: `1px solid ${semanticColor.borderDefault}`,
                }}
              />
              <Button
                variant="secondary"
                onClick={() =>
                  void copyToClipboard(uri).then(
                    () => setNotice('Provisioning URI copied.'),
                    () =>
                      setError(
                        'The provisioning URI could not be copied. Select and copy it manually.',
                      ),
                  )
                }
                style={{ minWidth: minTouchTarget, minHeight: minTouchTarget, marginTop: space[3] }}
              >
                Copy provisioning URI
              </Button>
              <form
                onSubmit={(event) => void confirm(event)}
                style={{ display: 'grid', gap: space[4], marginTop: space[4] }}
              >
                <h3>2. Confirm setup</h3>
                <Field
                  id="retailer-mfa-enrollment-code"
                  name="code"
                  label="Authenticator app code"
                  value={code}
                  onChange={(event) => setCode(event.currentTarget.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
                <Button
                  type="submit"
                  disabled={working || code.trim() === ''}
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                >
                  {working ? 'Confirming…' : 'Confirm authenticator app'}
                </Button>
              </form>
            </Card>
          )}
        </>
      )}
      {error === null ? null : <p role="alert">{error}</p>}
      {notice === null ? null : <p role="status">{notice}</p>}
      {recoveryCodes === null ? null : (
        <Card>
          <h2>Recovery codes</h2>
          <p role="alert">
            Save these codes now. They will not be shown again, and each code can be used only once.
          </p>
          <ul aria-label="One-time recovery codes">
            {recoveryCodes.map((recoveryCode) => (
              <li key={recoveryCode}>
                <code>{recoveryCode}</code>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}
